import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import firebase from 'firebase/compat/app';
import 'firebase/compat/messaging';
import { environment } from 'src/environments/environment';
import { LoginStatusService } from '../auth/login/login-status.service';
import { catchError, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { from, of } from 'rxjs';
import { UserNotificationService } from '../notofications/user-notification.service';
import { PrizeUser } from '../profile/prize-user.models';
import { PrizeStateService } from '../profile/prize-user.service';
import { CookieService } from 'ngx-cookie-service';
import { HOST_NAME } from 'src/app/global.tokens';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { PrizeEngineService } from './prize-engine.service';
import { PUSH_NOTIFICATION_STAT } from './base-http.service';

interface FcmTokenEntry {
  token: string;
  platform: string;
  date: string;
}

type SwNotificationAction = { action: string; title: string; icon?: string };

type SwNotificationOptions = NotificationOptions & {
  image?: string;          
  badge?: string;          
  vibrate?: number[];      
  actions?: SwNotificationAction[]; 
  renotify?: any
};

const PUSH_TOKEN_KEY_PREFIX = 'push_token_last_update:';
const keyForUser = (u: string) => PUSH_TOKEN_KEY_PREFIX + (u || '').toLowerCase();

@Injectable({ providedIn: 'root' })
export class PushTokenService {
  private messaging: firebase.messaging.Messaging;

  constructor(
    private loginStatus: LoginStatusService,
    private PrizeStateService: PrizeStateService,
    private userNotify: UserNotificationService,
    protected cookie: CookieService,
    private afAuth: AngularFireAuth,
    private prizeEngine: PrizeEngineService,
    @Inject(PLATFORM_ID) private platformId: Object,
    @Inject(HOST_NAME) private hostName: string,
  ) {
    if (isPlatformBrowser(this.platformId)) {
      if (!firebase.apps.length) {
        firebase.initializeApp(environment.firebaseConfig);
      }

      try {
        this.messaging = firebase.messaging();

        this.messaging.onMessage(async (payload: any) => {
          //console.log('[Push] Foreground message received:', payload);

          const d = payload?.data || {};
          if (Notification.permission !== 'granted') {
            //console.warn('[Push] Notification permission not granted');
            return;
          }

          const title = d.title || 'Notification';
          const body = d.body || '';
          const icon = d.icon || d.image || '/assets/icons/empty-inbox.png';

          const toBool = (v: any) => String(v).toLowerCase() === 'true';
          const parseActions = (s?: string) => {
            if (!s) return undefined;
            try {
              const arr = JSON.parse(s);
              return Array.isArray(arr) ? arr.slice(0, 2) : undefined;
            } catch {
              return undefined;
            }
          };
          const parseVibrate = (s?: string) => {
            if (!s) return undefined;
            const arr = String(s)
              .split(',')
              .map(x => parseInt(x.trim(), 10))
              .filter(n => Number.isFinite(n));
            return arr.length ? arr : undefined;
          };

          const opts: SwNotificationOptions = {
            body,
            icon,
            image: d.image || undefined,
            badge: d.badge || undefined,
            requireInteraction: d.require_interaction ? toBool(d.require_interaction) : undefined,
            renotify: d.renotify ? toBool(d.renotify) : undefined,
            tag: d.tag || undefined,
            data: d,
            actions: parseActions(d.actions),
          };

          (opts as any).vibrate = parseVibrate(d.vibrate);

          this.sendEvent('delivered', d);

          //console.log('[Push] Building notification options:', opts);

          try {
            const reg = await navigator.serviceWorker.getRegistration();
            if (reg) {
              //console.log('[Push] Showing notification via SW');
              await reg.showNotification(title, opts);
              return;
            }

            //console.warn('[Push] Fallback: showing notification directly');
            const { actions, ...fallbackOpts } = opts as any;
            const n = new Notification(title, fallbackOpts as NotificationOptions);

            n.onclick = (event: any) => {
              //console.log('[Push] Notification clicked (fallback)');
              const action = event?.action;
              let targetUrl = d.click_action_url;

              if (action && d.action_urls) {
                try {
                  const actionUrls = JSON.parse(d.action_urls);
                  if (actionUrls[action]) {
                    targetUrl = actionUrls[action];
                    //console.log(`[Push] Button clicked: ${action}, resolved URL: ${targetUrl}`);

                    this.sendEvent('clicked', d, targetUrl);
                  }
                } catch (e) {
                  console.error('[Push] Failed to parse action_urls:', e);
                }
              } else {
                //console.log('[Push] Body of notification clicked');
              }

              if (targetUrl) {
                if (targetUrl.startsWith('/')) {
                  targetUrl = window.location.origin + targetUrl;
                }
                //console.log('[Push] Opening URL:', targetUrl);

                this.sendEvent('opened', d, targetUrl);

                window.open(targetUrl, '_blank');
              }
            };
          } catch (e) {
            console.error('[Push] showNotification failed, fallback to page Notification:', e);
          }
        });
      }
      catch (err) {
        console.log(err);
      }
    }
  }

  private getStatsUrl(): string {
    const base = environment.production
      ? environment.uiBackend + this.hostName
      : environment.uiBackend;
    return `${base}${PUSH_NOTIFICATION_STAT}`;
  }

  public ensureTokenForUser() {
    if (!isPlatformBrowser(this.platformId)) {
      return of(null);
    }

    return this.loginStatus.getIfUserLogged().pipe(
      take(1),
      switchMap(user => {
        if (!user) {
          //console.warn('[Push] No user logged in');
          return of(null);
        }

        // const allowedUsers = [
        //   'tony.montana1@gmail.com',
        //   'ikoshak@gmail.com',
        //   'pizda123@wp.pl',
        //   'sojojax179@aperiol.com'
        // ];

        // if (!user.username || !allowedUsers.includes(user.username)) {
        //   console.warn('[Push] User not allowed for push:', user.username);
        //   return of(null);
        // }

        // const roles = (user.fullInfo?.roles as string[]) || [];
        // if (!roles.includes('test_acc') ) {
        //   console.warn('[Push] User does not have test_acc role');
        //   return of(null);
        // } else {
        //   //console.log('[Push] User have a test_acc role');
        // }

        if (Notification.permission === 'denied') {
          //console.warn('[Push] Notifications denied by user');
          //localStorage.removeItem(keyForUser(user.username))

          return of(null);
        }

        // const lastUpdate = localStorage.getItem(keyForUser(user.username));
        // if (lastUpdate) {
        //   const last = new Date(lastUpdate);
        //   const now  = new Date();
        //   const diffDays = (now.getTime() - last.getTime()) / (1000 * 60 * 60 * 24);
        //   if (diffDays < 2) {
        //     console.log(`[Push] Token for ${user.username} still fresh, ${diffDays.toFixed(1)}d`);
        //     return of(null);
        //   }
        // }

        if (Notification.permission === 'default') {
          return from(Notification.requestPermission()).pipe(
            switchMap(perm => {
              if (perm !== 'granted') {
                //console.warn('[Push] User did not grant permission');
                //this.userNotify.notify("pushPermissionRejected", { bet: "rejected" });

                return of(null);
              }

              return this.getAndSaveToken(user.username).pipe(
                switchMap(async (token) => {
                  const statsUrl = this.getStatsUrl();

                  const fbUser = await this.afAuth.currentUser;
                  const idToken = fbUser ? await fbUser.getIdToken() : null;

                  await this.sendPushMetaToSw(statsUrl, idToken);
                  return token;
                })
              );
            })
          );
        }

        return this.getAndSaveToken(user.username).pipe(
          switchMap(async (token) => {
            const statsUrl = this.getStatsUrl();

            const fbUser = await this.afAuth.currentUser;
            const idToken = fbUser ? await fbUser.getIdToken() : null;

            await this.sendPushMetaToSw(statsUrl, idToken);
            return token;
          })
        );
      }),
      catchError(err => {
        console.error('[Push] ensureTokenForUser failed', err);
        return of(null);
      })
    );
  }

  private getAndSaveToken(username?: string) {
    return from(
      navigator.serviceWorker.ready.then(reg =>
        this.messaging.getToken({
          vapidKey: environment.firebaseConfig.vapidKey,
          serviceWorkerRegistration: reg
        })
      )
    ).pipe(
      switchMap(token => {
        if (!token) {
          // console.warn('[Push] getToken returned empty');
          return of(null);
        }

        //console.log('[Push] token', token);

        const entry: FcmTokenEntry = {
          token,
          platform: navigator.userAgent,
          date: new Date().toISOString()
        };

        return this.PrizeStateService.getPrizeUser().pipe(
          take(1),
          // catchError(err => {
          //   console.warn('[Push] get user failed, will create fcm_tokens from scratch', err);
          //   return of({});
          // }),
          //tap((prizeUser) => console.log(`[Push] Prize user`, prizeUser)),
          switchMap(userDoc => {
            const current = Array.isArray(userDoc?.fcm_tokens) ? userDoc.fcm_tokens.filter(Boolean) : [];
            const alreadyHasToken = current.some(t => t?.token === entry.token);

            if (alreadyHasToken) {
              return of(token);
            }

            const updated = [...current, entry];

            return this.PrizeStateService.updatePrizeUser<any>({ fcm_tokens: updated }).pipe(
              map(() => {
                //console.log('[Push] Token saved/updated in backend:', entry);
                return token;
              })
            );
          })
        );
      }),
      catchError(err => {
        console.error('[Push] Failed to get/save token:', err);
        return of(null);
      })
    );
  }

  private async sendPushMetaToSw(statsUrl: string, authToken: string) {
    if (!navigator.serviceWorker) { console.error('[Push] Failed (sendPushMetaToSw) to get serviceWorker:'); return};

    const reg = await navigator.serviceWorker.ready;
    const target = reg.active || navigator.serviceWorker.controller;
    if (!target) { console.error('[Push] sendPushMetaToSw: no active SW'); return};

    target.postMessage({
      type: 'SET_PUSH_META',
      statsUrl,
      authToken,
    });
  }

  private buildEventPayload(
    event: 'delivered' | 'opened' | 'clicked' | 'failed',
    d: any,
    overridePath?: string
  ): any {
    let extra: any = {};

    const rawExtra = d.extra ?? d.EXTRA ?? null;
    if (rawExtra) {
      try {
        if (typeof rawExtra === 'string') {
          extra = JSON.parse(rawExtra);
        } else if (typeof rawExtra === 'object') {
          extra = rawExtra;
        }
      } catch {
        //
      }
    }

    const rawPath = overridePath || extra.path || d.click_action_url || '';
    const path = rawPath.replace(/^https?:\/\/[^/]+/i, '') || '';

    return {
      event,
      journey_id: extra.journey_id ||  '',
      step_id: extra.step_id       ||  '',
      path,
      language: extra.language     || null,
      tag: extra.tag               || null,
    };
  }

  private sendEvent(
    event: 'delivered' | 'opened' | 'clicked' | 'failed',
    d: any,
    overridePath?: string
  ): void {
    const body = this.buildEventPayload(event, d, overridePath);

    this.prizeEngine.post<typeof body, any>(PUSH_NOTIFICATION_STAT, body).subscribe({
      // next: () => console.log('[Push] Event sent', event, body),
      error: (err) =>
        console.error('[Push] Failed to send push event', event, err),
    });
  }

  // private getAndSaveToken() {
  // return from(
  //   navigator.serviceWorker.ready.then((registration) => {
  //     return this.messaging.getToken({
  //       vapidKey: environment.firebaseConfig.vapidKey,
  //       serviceWorkerRegistration: registration
  //     });
  //   })
  //   ).pipe(
  //     switchMap(token => {
  //       if (!token) {
  //         console.warn('[Push] getToken returned empty');
  //         return of(null);
  //       }

  //       console.warn('[Push] token', token);

  //       const entry: FcmTokenEntry = {
  //         token,
  //         platform: navigator.userAgent,
  //         date: new Date().toISOString()
  //       };

  //       localStorage.setItem(PUSH_TOKEN_KEY, entry.date);

  //       return this.prizeEngine.put<{ data: any }, any>(
  //         PRIZE_USER,
  //         { data: { fcm_tokens: [entry] } },
  //         { is_merge: true }
  //       ).pipe(
  //         map(() => {
  //           console.log('[Push] Token saved in backend:', entry);
  //           return token;
  //         })
  //       );
  //     }),
  //     catchError(err => {
  //       console.error('[Push] Failed to get/save token:', err);
  //       return of(null);
  //     })
  //   );

  // }
}
